import cx from 'classnames';
import {FC, useCallback, useEffect, useMemo} from 'react';
import PickerWheel from 'react-mobile-picker';
import {parseTime} from '~/common/utils';

import './TimePickerWheel.sass';

export type TimePickerWheelConfig = {
  hour: string;
  minute: string;
  meridiem: string;
};

const timePickerWheelOptions: Record<string, string[]> = {
  hour: Array.from({length: 12}, (_, i) => (i + 1).toString()),
  minute: Array.from({length: 60}, (_, i) => (i < 10 ? `0${i}` : i.toString())),
  meridiem: ['AM', 'PM'],
};

const DEFAULT_NUM_ROWS = 5;
const ROW_HEIGHT = 28;

interface TimePickerWheelProps {
  selectedTime?: string;
  onChange: (time: string) => void;
  numVisible?: number;
  'data-qa-id': string;
}

export const TimePickerWheel: FC<TimePickerWheelProps> = ({
  selectedTime,
  onChange,
  numVisible = DEFAULT_NUM_ROWS,
  'data-qa-id': dataQaId,
}) => {
  const timePieces: TimePickerWheelConfig = useMemo(() => {
    const defaultPieces = {hour: '6', minute: '00', meridiem: 'PM'};
    if (!selectedTime) return defaultPieces;
    const match: RegExpMatchArray | null = parseTime(selectedTime);
    if (!match?.groups) return defaultPieces;
    const {hour, minute = '00', meridiem} = match.groups;
    return {hour, minute, meridiem};
  }, [selectedTime]);

  const onMobileChangeTime = useCallback(
    (newValue: TimePickerWheelConfig) => {
      const {hour, minute, meridiem} = newValue;
      const newTime = `${hour}:${minute} ${meridiem}`;
      onChange(newTime);
    },
    [onChange]
  );

  useEffect(() => {
    const handleArrowPress = (e: KeyboardEvent) => {
      const picker = document.querySelector(`[data-qa-id="${dataQaId}"]`);
      if (!picker || !picker.contains(document.activeElement)) return;
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        // Capture up and down arrow keys to change time
        e.preventDefault();
        const focusedElement = document.activeElement as HTMLElement;
        if (focusedElement && focusedElement.classList.contains('time-picker-wheel__column')) {
          // Focused element is one of the time picker columns
          const column = focusedElement.dataset.qaId as keyof TimePickerWheelConfig;
          const items = focusedElement.querySelectorAll('.time-picker-wheel__item');
          if (items.length > 0 && column) {
            const selectedValue = timePieces[column];
            const currentIndex = Array.from(items).findIndex(
              (item) => (item as HTMLElement).dataset.qaValue === selectedValue
            );
            // Move up or down in the column
            const newIndex =
              e.key === 'ArrowUp'
                ? Math.max(currentIndex - 1, 0)
                : Math.min(currentIndex + 1, items.length - 1);
            const newValue: TimePickerWheelConfig = {
              ...timePieces,
              [column]: (items[newIndex] as HTMLElement).dataset.qaValue ?? timePieces[column],
            };
            // Update the time
            onMobileChangeTime(newValue);
          }
        }
      }
    };

    document.body.addEventListener('keydown', handleArrowPress);
    return () => {
      document.body.removeEventListener('keydown', handleArrowPress);
    };
  }, [dataQaId, timePieces, onMobileChangeTime]);

  useEffect(() => {
    const hourPicker: HTMLElement | null = document.querySelector(
      `[data-qa-id="${dataQaId}"] #time-picker-wheel__column-hour`
    );
    const minutePicker: HTMLElement | null = document.querySelector(
      `[data-qa-id="${dataQaId}"] #time-picker-wheel__column-minute`
    );
    const meridiemPicker: HTMLElement | null = document.querySelector(
      `[data-qa-id="${dataQaId}"] #time-picker-wheel__column-meridiem`
    );

    if (!hourPicker || !minutePicker || !meridiemPicker) return undefined;

    const centerOnFocus = (e: FocusEvent) => {
      e.preventDefault();
      (e.target as HTMLElement).scrollIntoView({
        behavior: 'instant',
        block: 'center',
        inline: 'center',
      });
    };

    hourPicker.addEventListener('focus', centerOnFocus);
    minutePicker.addEventListener('focus', centerOnFocus);
    meridiemPicker.addEventListener('focus', centerOnFocus);

    return () => {
      hourPicker.removeEventListener('focus', centerOnFocus);
      minutePicker.removeEventListener('focus', centerOnFocus);
      meridiemPicker.removeEventListener('focus', centerOnFocus);
    };
  }, [dataQaId]);

  return (
    <PickerWheel
      value={timePieces}
      onChange={onMobileChangeTime}
      wheelMode="normal"
      className="time-picker-wheel"
      height={ROW_HEIGHT * numVisible}
      itemHeight={ROW_HEIGHT}
      data-qa-id={dataQaId}
    >
      {Object.entries(timePickerWheelOptions).map(([name, options]) => (
        <PickerWheel.Column
          key={name}
          name={name}
          id={`time-picker-wheel__column-${name}`}
          className="time-picker-wheel__column"
          data-qa-id={name}
          tabIndex={0}
          role="listbox"
        >
          {options.map((option) => (
            <PickerWheel.Item
              key={option}
              value={option}
              className={cx('time-picker-wheel__item', `time-picker-wheel__${name}-option`)}
              data-qa-value={option}
              role="option"
            >
              {option}
            </PickerWheel.Item>
          ))}
        </PickerWheel.Column>
      ))}
    </PickerWheel>
  );
};
